import { useLayoutEffect, useRef, useState, type ReactNode } from 'react'

interface Props {
  /** Смена ключа — смена состояния: содержимое проявляется заново. */
  stateKey: string
  children: ReactNode
}

/**
 * Блок, который меняет наполнение плавно: высота перетекает к новому
 * содержимому, само содержимое мягко проявляется. Нужен там, где одно место
 * проходит несколько состояний подряд (изменения → проверка → итог), чтобы это
 * читалось как один компонент, а не как подмена карточек.
 */
export const SmoothHeight = ({ stateKey, children }: Props) => {
  const innerRef = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState<number | null>(null)
  useLayoutEffect(() => {
    const el = innerRef.current
    if (!el) return
    const measure = () => setHeight(el.offsetHeight)
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])
  return (
    // `shrink-0`: в колонке с ограниченной высотой (окно публикации — flex с
    // `max-h` и прокруткой) блок с `overflow-hidden` теряет min-height:auto и
    // сжимается — содержимое обрезалось посередине.
    <div
      className="shrink-0 overflow-hidden transition-[height] duration-300 ease-out motion-reduce:transition-none"
      style={height === null ? undefined : { height }}
    >
      <div ref={innerRef}>
        <div key={stateKey} className="flex flex-col gap-4 duration-300 animate-in fade-in">
          {children}
        </div>
      </div>
    </div>
  )
}
